import { BaseTranscriptClient, type TranscriptClient } from 'src/models/TranscriptClient'
import { Transcript } from 'src/models/Transcript'
import { DummyTranscriptUserStateManager } from 'src/models/TranscriptUserState'
import { TranscriptConnectionStateManager, CONNECTING_TRANSCRIPT_CONNECTION_STATE } from 'src/models/TranscriptConnectionState'
import { debug } from 'src/services/DebugService'

import { VisorHttpClientInterface } from './HttpClientInterface'

type DocumentTranscriptClientOptions = {
    transcriptionDocumentId: string
    visorHttpClient: VisorHttpClientInterface
}

export class DocumentTranscriptClient extends BaseTranscriptClient {
    readonly source = 'document' as const
    private _transcriptionDocumentId: string
    private _visorHttpClient: VisorHttpClientInterface
    private _connectionStateManager: TranscriptConnectionStateManager
    private _userStateManager = new DummyTranscriptUserStateManager()
    private _pollingTimeoutRef: NodeJS.Timeout | null = null
    private _pollingInterval: number = 10000 // 10 seconds
    private _isPolling: boolean = false
    private _isDestroyed: boolean = false

    constructor({ transcriptionDocumentId, visorHttpClient }: DocumentTranscriptClientOptions) {
        super()
        this._transcriptionDocumentId = transcriptionDocumentId
        this._visorHttpClient = visorHttpClient
        this._connectionStateManager = new TranscriptConnectionStateManager(CONNECTING_TRANSCRIPT_CONNECTION_STATE)
    }

    init = async () => {
        debug.log('[DocumentTranscriptClient] Initializing polling for transcript', this._transcriptionDocumentId)

        this._connectionStateManager.setConnectionState({
            status: 'connecting',
            isInitialized: false,
        })

        // Start polling immediately
        void this._pollForTranscript()
    }

    destroy = () => {
        this._isDestroyed = true

        if (this._pollingTimeoutRef) {
            clearTimeout(this._pollingTimeoutRef)
            this._pollingTimeoutRef = null
        }

        debug.log('[DocumentTranscriptClient] Destroyed')
    }

    getUserState = () => this._userStateManager

    getConnectionState = () => this._connectionStateManager

    applyOps: TranscriptClient['applyOps'] = () => {
        throw new Error('Editing is not supported in document view mode')
    }

    // No write path. Ignore the editor write guard and leave isReadOnly true.
    setReadOnly = (isReadOnly: boolean) => {
        void isReadOnly
    }

    undo = () => {
        // Document mode is read-only, so undo is a no-op
    }

    redo = () => {
        // Document mode is read-only, so redo is a no-op
    }

    private _pollForTranscript = async () => {
        // Prevent concurrent polls
        if (this._isPolling) {
            debug.log('[DocumentTranscriptClient] Poll already in progress, skipping')

            return
        }

        // Prevent polling if destroyed
        if (this._isDestroyed) {
            debug.log('[DocumentTranscriptClient] Client destroyed, skipping poll')

            return
        }

        this._isPolling = true

        try {
            debug.log('[DocumentTranscriptClient] Polling for transcript', this._transcriptionDocumentId)

            const transcript = await this._visorHttpClient.getTranscript(this._transcriptionDocumentId)

            // Check if destroyed after async operation
            if (this._isDestroyed) {
                debug.log('[DocumentTranscriptClient] Client destroyed during fetch, aborting')

                return
            }

            // Mark as initialized after first successful fetch, regardless of whether transcript has content
            // This allows the UI to show the "processing" message instead of a loading spinner
            this._connectionStateManager.setConnectionState({
                status: 'connected',
                isInitialized: true,
            })

            // Check if transcript is empty (no paragraphs)
            if (this._isTranscriptEmpty(transcript)) {
                debug.log('[DocumentTranscriptClient] Transcript is empty, scheduling next poll')

                // Schedule next poll
                this._scheduleNextPoll()

                return
            }

            this._transcript = transcript

            // Emit events to notify subscribers that transcript is loaded
            this.emitEvents([
                {
                    type: 'map',
                    isLocal: false,
                    tags: [],
                    path: [],
                    delta: [{ change: ['paragraphIds', 'paragraphs', 'speakers'] }],
                },
                {
                    type: 'array',
                    isLocal: false,
                    tags: [],
                    path: ['paragraphIds'],
                    delta: [{ insert: this.getParagraphIds() }],
                },
            ])

            debug.log('[DocumentTranscriptClient] Transcript loaded successfully', {
                paragraphCount: this.getTotalParagraphCount(),
            })
        } catch (error) {
            console.error('[DocumentTranscriptClient] Error polling for transcript:', error)

            // On error, we continue polling to handle transient network errors.
            // In case of document parsing error on the backend, `MetadataContext` will handle it and show the message to the user.
            // The connection state will remain 'connecting' until transcript is successfully loaded
            this._scheduleNextPoll()
        } finally {
            this._isPolling = false
        }
    }

    private _scheduleNextPoll = () => {
        // Don't schedule if destroyed
        if (this._isDestroyed) {
            debug.log('[DocumentTranscriptClient] Client destroyed, not scheduling next poll')

            return
        }

        this._pollingTimeoutRef = setTimeout(() => {
            this._pollForTranscript()
        }, this._pollingInterval)
    }

    private _isTranscriptEmpty = (transcript: Transcript): boolean => {
        return transcript.paragraphIds.length === 0
    }
}
